/**
 * Shared newsletter subscribe — used by the footer, homepage section,
 * MCx landing section, and the site-wide popup.
 *
 * Every newsletter surface posts to ONE Klaviyo list (settings
 * klaviyo_newsletter_list_id, default XMS7Qq). Affinity keys mirror
 * the maker waitlist exactly — `interested_in_<handle>` with dashes
 * converted to underscores — so newsletter and waitlist signal unify.
 *
 * Activation: any form (or container holding a form) carrying
 * `data-newsletter-subscribe` is wired automatically on submit.
 *
 * Read from the form/container's data-* attributes:
 *   data-klaviyo-key      Klaviyo public API key
 *   data-klaviyo-list     Newsletter list ID
 *   data-source-type      maker | brand | mcx_line | popup | home | (any)
 *   data-source-handle    e.g. "kamon_knives" (already underscored)
 *   data-extra-handles    optional comma-separated extras, e.g. "mcx"
 *   data-success-text     success message (double opt-in copy)
 *
 * Status reporting: the script writes to the first
 * `[data-newsletter-status]` element under the form's parent.
 */
(function () {
  if (typeof document === 'undefined') return;

  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  function propName(handle) {
    return 'interested_in_' + String(handle || '').replace(/-/g, '_');
  }

  function findStatus(form) {
    if (form.dataset.statusSelector) {
      var n = document.querySelector(form.dataset.statusSelector);
      if (n) return n;
    }
    return (form.parentElement && form.parentElement.querySelector('[data-newsletter-status]')) ||
           form.querySelector('[data-newsletter-status]');
  }

  function setStatus(form, message, state) {
    var status = findStatus(form);
    if (!status) return;
    status.textContent = message;
    if (state) status.setAttribute('data-state', state);
    else status.removeAttribute('data-state');
  }

  function buildProperties(form) {
    var props = {};
    var handle = (form.dataset.sourceHandle || '').trim();
    if (handle) props[propName(handle)] = true;
    var extras = (form.dataset.extraHandles || '').split(',');
    for (var i = 0; i < extras.length; i++) {
      var h = extras[i].trim();
      if (h) props[propName(h)] = true;
    }
    return props;
  }

  function fieldValue(form, names) {
    for (var i = 0; i < names.length; i++) {
      var n = form.querySelector('input[name="' + names[i] + '"]');
      if (n && n.value) return n.value.trim();
    }
    return '';
  }

  function clearInvalid(form) {
    form.querySelectorAll('.is-invalid').forEach(function (el) {
      el.classList.remove('is-invalid');
    });
  }
  function markInvalid(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    if (el) {
      el.classList.add('is-invalid');
      try { el.focus(); } catch (e) {}
    }
  }

  async function subscribe(form) {
    var emailInput = form.querySelector('input[type="email"], input[name="email"]');
    var email = emailInput ? emailInput.value.trim() : '';
    var firstName = fieldValue(form, ['first_name', 'firstName']);
    var lastName = fieldValue(form, ['last_name', 'lastName']);
    var klaviyoKey = (form.dataset.klaviyoKey || '').trim();
    var klaviyoList = (form.dataset.klaviyoList || '').trim();
    var successText = form.dataset.successText ||
      'Check your email to confirm your subscription.';

    clearInvalid(form);

    // The footer band marks first_name + last_name as `required`, but the
    // form carries `novalidate` so HTML5 doesn't block. Enforce manually
    // for any `required` text-style input on the form — this also covers
    // future surfaces that opt into requiring name fields. Email is
    // always checked separately (format too).
    var firstInput = form.querySelector('input[name="first_name"]');
    if (firstInput && firstInput.required && !firstName) {
      markInvalid(form, 'first_name');
      setStatus(form, 'Enter your first name.', 'error');
      return false;
    }
    var lastInput = form.querySelector('input[name="last_name"]');
    if (lastInput && lastInput.required && !lastName) {
      markInvalid(form, 'last_name');
      setStatus(form, 'Enter your last name.', 'error');
      return false;
    }

    if (!email || !EMAIL_RE.test(email)) {
      markInvalid(form, 'email');
      setStatus(form, 'Enter a valid email address.', 'error');
      return false;
    }
    if (!klaviyoKey || !klaviyoList) {
      setStatus(form, 'Newsletter is not configured. Try again later.', 'error');
      if (window.console) console.warn('[newsletter] missing klaviyo key or list');
      return false;
    }

    var submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    setStatus(form, 'Subscribing…');

    try {
      var properties = buildProperties(form);
      var profileAttrs = { email: email };
      if (firstName) profileAttrs.first_name = firstName;
      if (lastName) profileAttrs.last_name = lastName;
      if (Object.keys(properties).length) profileAttrs.properties = properties;

      var body = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: { type: 'profile', attributes: profileAttrs }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: klaviyoList } }
          }
        }
      };

      var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' +
                encodeURIComponent(klaviyoKey);
      var response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Revision': '2024-10-15'
        },
        body: JSON.stringify(body)
      });

      if (response.ok || response.status === 202) {
        try { form.reset(); } catch (e) {}
        setStatus(form, successText, 'success');
        try {
          localStorage.setItem('mc_nl_popup_subscribed', '1');
          window.dispatchEvent(new CustomEvent('newsletter:subscribed', {
            detail: {
              email: email,
              first_name: firstName,
              last_name: lastName,
              source_type: form.dataset.sourceType || '',
              source_handle: form.dataset.sourceHandle || ''
            }
          }));
        } catch (e) {}
        return true;
      }

      var err = '';
      try { err = await response.text(); } catch (e) {}
      setStatus(form, 'Something went wrong. Try again in a moment.', 'error');
      if (window.console) console.warn('[newsletter] Klaviyo error', response.status, err);
      return false;
    } catch (e) {
      setStatus(form, "We couldn't sign you up — this is usually an ad-blocker or privacy extension. Please pause it for this page, or try a different browser, and try again.", 'error');
      if (window.console) console.warn('[newsletter]', e);
      return false;
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('[data-newsletter-subscribe]')) return;
    event.preventDefault();
    subscribe(form);
  });

  // Expose for the popup module (no global pollution beyond one name).
  window.ModernCookingNewsletter = { subscribe: subscribe, propName: propName };
})();
